import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { crewGroups as demoCrewGroups, instructions as demoInstructions, kitGroups as demoKitGroups, production as demoProduction, productionFiles as demoProductionFiles, schedule as demoSchedule, shotSections as demoShotSections } from '../data/production';
import type { CrewGroup, KitGroup, ProductionFile, ScheduleItem, ScheduleStatus, ShotSection } from '../types/shoot';
import { advanceSchedule, fetchShootDay, getTokenFromUrl, resumeAutoSchedule, toggleKitItem as remoteToggleKitItem, toggleShot as remoteToggleShot, type RemoteProductionFiles, type RemoteShootDay } from '../utils/shootDayApi';

/* Near-real-time, not websockets: every device polls this often, and also
   refetches right after its own action. Good enough for a shared checklist
   on one shoot day, far simpler and more robust than wiring up Postgres
   Realtime under RLS-denied tables — see the handoff notes for why. */
const POLL_MS = 15000;
interface ProductionInfo {
  studio: string;
  title: string;
  /** 9 Sep 2026: the project/episode name alone (e.g. "Economy of Scale
      03"), distinct from `title`'s combined "Client — Project" string.
      Today's header shows the client name as its own label above the big
      title, so the title itself only needs to say the episode/project name
      — using the combined `title` there would repeat the client a second
      time right underneath it. Falls back to `title` wherever the source
      project name is missing (e.g. demo data predating this field). */
  episodeTitle: string;
  client: string;
  location: string;
  address: string;
  /** A pasted Google Maps (or similar) link — used directly for "Open
      Location" when present, instead of a maps search built from address. */
  locationMapUrl: string;
  parkingNotes: string;
  date: string;
  producer: {
    name: string;
    phone: string;
  } | null;
}
interface ShootState {
  loading: boolean;
  /**
   * 'demo' means no token was recognised — the static example data below,
   * shown so the design can still be opened and judged on its own. Crew and
   * staff were merged into one full-access 'crew' tier 9 Sep 2026; 'client'
   * is the newer, lighter tier (no Shots, no Kit, no Production) — see
   * shootDayApi.ts's RemoteShootDay.role.
   */
  role: 'crew' | 'client' | 'demo';
  tokenGivenButNotFound: boolean;
  production: ProductionInfo;
  schedule: ScheduleItem[];
  crewGroups: CrewGroup[];
  shotSections: ShotSection[];
  kitGroups: KitGroup[];
  instructions: string[];
  productionFiles: ProductionFile[];
  /**
   * The schedule index treated as "Running now". On the shoot's own day
   * this is either the live clock-computed position (see clockIndexFor,
   * when scheduleMode is 'auto') or exactly what a crew member last set it
   * to (when scheduleMode is 'manual') — never a blend of the two, so a
   * manual correction actually sticks instead of being overridden back by
   * the clock on the next tick. Outside the shoot's own day it's forced
   * out of range (-1 or schedule.length) by shootDayPhase below.
   */
  nowIndex: number;
  /**
   * Whether nowIndex is currently clock-driven ('auto', the default) or
   * pinned to whatever a crew member last explicitly set via setCurrentTo
   * ('manual') — 9 Sep 2026, added so a manual correction (e.g. the shoot
   * is running behind and the clock is marking the wrong item current)
   * actually holds until someone taps Resume Auto, rather than the clock
   * silently reclaiming control on the next recompute. Always 'auto' in
   * demo mode.
   */
  scheduleMode: 'auto' | 'manual';
  /**
   * Where "now" sits relative to the shoot's own calendar date — 9 Sep 2026,
   * added after the manually-advanced current_schedule_index was twice
   * found showing a stale "Running now" position on a shoot that was still
   * days away. `null` in demo mode (no real date to compare against).
   * 'before'/'after' mean nowIndex is intentionally out of range (-1, or
   * one past the last item) so nothing reads as "current" outside the
   * shoot's actual day, regardless of what current_schedule_index holds
   * from earlier testing.
   */
  shootDayPhase: 'before' | 'today' | 'after' | null;
  /** Sets "current" to any index — forward or backward — and switches to
      'manual' mode so the clock stops overriding it. Used both for a quick
      "advance to next" action and for correcting the position directly
      when a shoot is running behind or ahead of its own schedule. */
  setCurrentTo: (index: number) => void;
  /** Hands control of "current" back to the live clock (scheduleMode ->
      'auto'). No-op outside a real crew/staff session. */
  resumeAuto: () => void;
  statusFor: (index: number) => ScheduleStatus;
  isShotChecked: (id: string) => boolean;
  toggleShot: (id: string) => void;
  isKitChecked: (id: string) => boolean;
  toggleKit: (id: string) => void;
  countChecked: (ids: string[], kind: 'shot' | 'kit') => number;
}
const ShootContext = createContext<ShootState | null>(null);
function buildFiles(files: RemoteProductionFiles | null | undefined): ProductionFile[] {
  if (!files) return [];
  const rows: ProductionFile[] = [];
  if (files.scriptUrl) rows.push({
    id: 'script',
    title: 'Script',
    meta: 'Open link',
    url: files.scriptUrl
  });
  if (files.guestBiosUrl) rows.push({
    id: 'bios',
    title: 'Guest Bios',
    meta: 'Open link',
    url: files.guestBiosUrl
  });
  if (files.storyboardUrl) rows.push({
    id: 'storyboard',
    title: 'Storyboard',
    meta: 'Open link',
    url: files.storyboardUrl
  });
  if (files.dropboxUrl) rows.push({
    id: 'dropbox',
    title: 'Dropbox Folder',
    meta: 'Open link',
    url: files.dropboxUrl
  });
  return rows;
}

/** One free-text field in the builder, rendered as one bullet per line. */
function lines(value: string | null | undefined): string[] {
  return (value ?? '').split('\n').map(line => line.trim()).filter(Boolean);
}

/** The viewer's own local calendar date as YYYY-MM-DD — matches the plain
    `date` column Postgres hands back for production.date, so the two can
    be compared as strings with no timezone-shifted Date parsing involved. */
function todayLocalISO(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parses a schedule time label such as "07:00" or "7:00 AM" into minutes
    since local midnight, so it can be compared against the viewer's own
    clock. Returns null for anything that doesn't match either shape, so a
    malformed time_label can't accidentally jump the auto-advance forward. */
function parseTimeToMinutes(label: string | undefined | null): number | null {
  if (!label) return null;
  const trimmed = label.trim();
  const withAmPm = trimmed.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (withAmPm) {
    let hours = parseInt(withAmPm[1], 10) % 12;
    const minutes = parseInt(withAmPm[2], 10);
    if (/pm/i.test(withAmPm[3])) hours += 12;
    return hours * 60 + minutes;
  }
  const bare = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (bare) {
    const hours = parseInt(bare[1], 10);
    const minutes = parseInt(bare[2], 10);
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
  }
  return null;
}

/** The viewer's own local time as minutes since midnight — same units as
    parseTimeToMinutes, so schedule times and "now" compare directly. */
function nowMinutesLocal(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

/**
 * 9 Sep 2026, at Tom's request: on the shoot's own day, "current" now
 * auto-advances by the clock instead of relying purely on someone tapping
 * Advance. This finds which item's time window the viewer's local "now"
 * falls into: -1 before the first item's start, `schedule.length` once the
 * last item's end time has passed, and otherwise the index of the last
 * item whose start time has arrived (so a gap between two items still
 * shows the earlier one as current rather than nothing).
 *
 * Trade-off Tom explicitly accepted: on its own, this can mark the wrong
 * item "current" whenever the shoot is actually running behind or ahead of
 * its schedule, since it only knows the clock, not what's really happening
 * on set. That's exactly what scheduleMode/setCurrentTo (below, and in
 * shootDayApi.ts) are for — a crew member can tap any item to correct
 * "current" directly, including backward, and that pins nowIndex to their
 * choice (scheduleMode: 'manual') until someone explicitly hits Resume
 * Auto — this function is only consulted at all while scheduleMode is
 * 'auto'.
 */
function clockIndexFor(schedule: ScheduleItem[], nowMinutes: number): number {
  if (schedule.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < schedule.length; i++) {
    const startMin = parseTimeToMinutes(schedule[i].start);
    if (startMin !== null && startMin <= nowMinutes) idx = i;
  }
  const last = schedule[schedule.length - 1];
  const lastEndMin = parseTimeToMinutes(last.end) ?? parseTimeToMinutes(last.start);
  if (lastEndMin !== null && nowMinutes >= lastEndMin) return schedule.length;
  return idx;
}
function mapRemote(remote: RemoteShootDay) {
  const schedule: ScheduleItem[] = remote.schedule.map(item => ({
    id: item.id,
    title: item.title,
    start: item.timeLabel,
    end: item.endTimeLabel ?? undefined,
    duration: item.durationNote ?? '',
    who: item.peopleNote ?? '',
    where: item.locationNote ?? '',
    note: item.note ?? ''
  }));
  const portalMembers = remote.crew.map((member, index) => ({
    id: `crew-${index}`,
    name: member.name,
    role: member.role ?? '',
    phone: member.phone ?? ''
  }));
  const extraOnSite = (remote.extraCrew ?? []).filter(member => !member.remote);
  const extraRemote = (remote.extraCrew ?? []).filter(member => member.remote);
  const crewGroups: CrewGroup[] = [{
    id: 'portal',
    title: 'Crew',
    members: [...portalMembers, ...extraOnSite.map((member, index) => ({
      id: `extra-${index}`,
      name: member.name,
      role: member.role ?? '',
      phone: member.phone ?? ''
    }))]
  }];
  if (extraRemote.length > 0) {
    crewGroups.push({
      id: 'remote',
      title: 'Remote',
      members: extraRemote.map((member, index) => ({
        id: `remote-${index}`,
        name: member.name,
        role: member.role ?? '',
        phone: member.phone ?? ''
      }))
    });
  }
  if ((remote.clientContacts ?? []).length > 0) {
    crewGroups.push({
      id: 'client',
      title: 'Client Contacts',
      members: remote.clientContacts.map((contact, index) => ({
        id: `client-${index}`,
        name: contact.name,
        role: contact.role || remote.production.clientName || '',
        phone: contact.phone ?? ''
      }))
    });
  }
  if ((remote.guests ?? []).length > 0) {
    crewGroups.push({
      id: 'guests',
      title: 'Featured Guests',
      members: remote.guests.map((guest, index) => ({
        id: `guest-${index}`,
        name: guest.name,
        role: guest.role ?? '',
        phone: guest.phone ?? ''
      }))
    });
  }

  // 11 Sep 2026, at Tom's request: the per-section hint line ("Must be in
  // the can before wrap", etc.) was removed outright — ShotSection no
  // longer carries a caption at all (see types/shoot.ts / Shots.tsx).
  const shotSections: ShotSection[] = (['Essential', 'B-Roll', 'Optional'] as const).map(section => ({
    id: section.toLowerCase().replace(/[\s-]/g, ''),
    title: section,
    items: remote.shots.filter(shot => shot.section === section).map(shot => ({
      id: shot.id,
      title: shot.name,
      camera: shot.cameraLabel ?? '',
      image: shot.referenceImageUrl ?? ''
    }))
  }));
  const kitGroups: KitGroup[] = ['Camera', 'Audio', 'Lighting', 'Media/Power'].map(section => ({
    id: section.toLowerCase().replace(/\//g, '-'),
    title: section,
    items: remote.kit.filter(item => item.section === section).map(item => ({
      id: item.id,
      name: item.name,
      qty: item.quantity ?? '',
      owner: item.owner ?? ''
    }))
  }));
  const producerMember = remote.crew.find(m => (m.role ?? '').toLowerCase().includes('producer') && m.phone);
  const anyWithPhone = remote.crew.find(m => m.phone);
  const chosenProducer = producerMember ?? anyWithPhone;
  const producer = chosenProducer ? {
    name: chosenProducer.name,
    phone: chosenProducer.phone as string
  } : null;
  const title = [remote.production.clientName, remote.production.projectName].filter(Boolean).join(' — ');
  const production: ProductionInfo = {
    studio: 'Portal Productions',
    title,
    episodeTitle: remote.production.projectName || title,
    client: remote.production.clientName ?? '',
    location: remote.production.location ?? '',
    address: remote.production.locationAddress ?? remote.production.location ?? '',
    locationMapUrl: remote.production.locationMapUrl ?? '',
    parkingNotes: remote.production.parkingNotes ?? '',
    date: remote.production.date ?? '',
    producer
  };
  return {
    production,
    schedule,
    crewGroups,
    shotSections,
    kitGroups,
    instructions: lines(remote.productionStaffOnly?.importantInstructions),
    productionFiles: buildFiles(remote.productionStaffOnly?.files)
  };
}
export function ShootProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const token = useMemo(() => getTokenFromUrl(), []);
  const [remote, setRemote] = useState<RemoteShootDay | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [tokenGivenButNotFound, setTokenGivenButNotFound] = useState(false);
  const pollRef = useRef<number | null>(null);
  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    const data = await fetchShootDay(token);
    if (!data) {
      setTokenGivenButNotFound(true);
      setLoading(false);
      return;
    }
    setTokenGivenButNotFound(false);
    setRemote(data);
    setLoading(false);
  }, [token]);
  useEffect(() => {
    load();
    if (!token) return;
    pollRef.current = window.setInterval(load, POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [token, load]);

  /* Forces nowIndex to recompute periodically so the clock-driven
     auto-advance (see clockIndexFor) moves on its own instead of only
     updating on the next poll or user interaction. Only the tick itself
     matters, not its value. */
  const [clockTick, setClockTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setClockTick(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);
  const mapped = useMemo(() => remote ? mapRemote(remote) : null, [remote]);
  const production = mapped?.production ?? demoProduction as unknown as ProductionInfo;
  const schedule = mapped?.schedule ?? demoSchedule;
  const crewGroups = mapped?.crewGroups ?? demoCrewGroups;
  const shotSections = mapped?.shotSections ?? demoShotSections;
  const kitGroups = mapped?.kitGroups ?? demoKitGroups;
  const instructions = mapped?.instructions ?? demoInstructions;
  const productionFiles = mapped?.productionFiles ?? demoProductionFiles;
  const shootDayPhase = useMemo<'before' | 'today' | 'after' | null>(() => {
    if (!remote || !remote.production.date) return null;
    const today = todayLocalISO();
    if (today < remote.production.date) return 'before';
    if (today > remote.production.date) return 'after';
    return 'today';
  }, [remote]);
  const scheduleMode = remote?.scheduleMode ?? 'auto';
  const nowIndex = useMemo(() => {
    if (!remote) return 2;
    if (shootDayPhase === 'before') return -1;
    if (shootDayPhase === 'after') return schedule.length;
    if (remote.scheduleMode === 'manual') {
      return Math.min(Math.max(remote.currentScheduleIndex, 0), Math.max(schedule.length - 1, 0));
    }
    return clockIndexFor(schedule, nowMinutesLocal());
    // clockTick isn't read directly — it's a dependency purely to force this
    // to recompute every 30s so the clock-driven position keeps moving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote, shootDayPhase, schedule, clockTick]);
  const setCurrentTo = useCallback((index: number) => {
    if (!remote || !token) return;
    const clamped = Math.min(Math.max(index, 0), Math.max(schedule.length - 1, 0));
    setRemote(current => current ? {
      ...current,
      currentScheduleIndex: clamped,
      scheduleMode: 'manual'
    } : current);
    void advanceSchedule(token, clamped);
  }, [remote, token, schedule.length]);
  const resumeAuto = useCallback(() => {
    if (!remote || !token) return;
    setRemote(current => current ? {
      ...current,
      scheduleMode: 'auto'
    } : current);
    void resumeAutoSchedule(token);
  }, [remote, token]);
  const statusFor = useCallback((index: number): ScheduleStatus => index < nowIndex ? 'complete' : index === nowIndex ? 'current' : 'upcoming', [nowIndex]);
  const [demoChecked, setDemoChecked] = useState<Record<string, boolean>>({
    sh1: true,
    k1: true,
    k2: true,
    k3: true,
    k5: true
  });
  const isShotChecked = useCallback((id: string) => remote ? Boolean(remote.shots.find(s => s.id === id)?.checked) : Boolean(demoChecked[id]), [remote, demoChecked]);
  const toggleShotFn = useCallback((id: string) => {
    if (!remote || !token) {
      setDemoChecked(prev => ({
        ...prev,
        [id]: !prev[id]
      }));
      return;
    }
    const next = !remote.shots.find(s => s.id === id)?.checked;
    setRemote(current => current ? {
      ...current,
      shots: current.shots.map(s => s.id === id ? {
        ...s,
        checked: next
      } : s)
    } : current);
    void remoteToggleShot(token, id, next);
  }, [remote, token]);
  const isKitChecked = useCallback((id: string) => remote ? Boolean(remote.kit.find(k => k.id === id)?.checked) : Boolean(demoChecked[id]), [remote, demoChecked]);
  const toggleKitFn = useCallback((id: string) => {
    if (!remote || !token) {
      setDemoChecked(prev => ({
        ...prev,
        [id]: !prev[id]
      }));
      return;
    }
    const next = !remote.kit.find(k => k.id === id)?.checked;
    setRemote(current => current ? {
      ...current,
      kit: current.kit.map(k => k.id === id ? {
        ...k,
        checked: next
      } : k)
    } : current);
    void remoteToggleKitItem(token, id, next);
  }, [remote, token]);
  const countChecked = useCallback((ids: string[], kind: 'shot' | 'kit') => ids.reduce((total, id) => total + ((kind === 'shot' ? isShotChecked(id) : isKitChecked(id)) ? 1 : 0), 0), [isShotChecked, isKitChecked]);
  const value: ShootState = {
    loading,
    role: remote ? remote.role : 'demo',
    tokenGivenButNotFound,
    production,
    schedule,
    crewGroups,
    shotSections,
    kitGroups,
    instructions,
    productionFiles,
    nowIndex,
    scheduleMode,
    shootDayPhase,
    setCurrentTo,
    resumeAuto,
    statusFor,
    isShotChecked,
    toggleShot: toggleShotFn,
    isKitChecked,
    toggleKit: toggleKitFn,
    countChecked
  };
  return <ShootContext.Provider value={value} data-id="element-53" data-mp="2peehu6wbkz1d">{children}</ShootContext.Provider>;
}
export function useShoot(): ShootState {
  const ctx = useContext(ShootContext);
  if (!ctx) throw new Error('useShoot must be used inside ShootProvider');
  return ctx;
}